// Route-Kontext EINES Tabs (docs/shell.md).
//
// Das Problem, das diese Datei loest: der Router ist ein Singleton mit
// genau einem `params`. Solange immer nur eine Seite sichtbar war, war
// das richtig. In der Shell koennen ZWEI Seiten gleichzeitig sichtbar
// sein -- links Host "forge", rechts Host "worker1". Beide wuerden aus
// demselben router.params lesen und dieselben Daten zeigen.
//
// Deshalb bekommt jede Seite ihre Identitaet vom Wirt (PageHost) in den
// Kontext gelegt und liest sie von dort. Der Router bleibt die Quelle
// fuer "welcher Tab hat den Fokus" (Adresszeile, Verlauf) -- aber nicht
// mehr fuer "welches Ding zeigt DIESE Flaeche".
//
// Betroffen sind nur die zwei Routen mit Parametern
// (/infra/cluster/:host_id, /zuhause/aktoren/:name). Alle anderen
// Seiten muessen nichts tun.

import { getContext, hasContext, setContext } from "svelte";

import { fensterSichtbar } from "../fenster-sichtbar.svelte";
import { router } from "../router.svelte";
import { tabWurzelFuer } from "../routes";

const KEY = Symbol.for("shell.page-route");

export interface PageRoute {
  path: string;
  params: Record<string, string>;
}

/** Vom Wirt gerufen, einmal je Tab. */
export function setPageRoute(route: () => PageRoute): void {
  setContext(KEY, route);
}

/** Von der Seite gerufen. Ohne Wirt (Login-Gate, Tests) faellt sie auf
 *  den globalen Router zurueck -- so bleibt jede Seite auch ausserhalb
 *  der Shell lauffaehig. */
export function pageRoute(): PageRoute {
  if (!hasContext(KEY)) {
    return { path: router.current, params: router.params };
  }
  return getContext<() => PageRoute>(KEY)();
}

const VORN = Symbol.for("shell.seite-vorn");

/** Von TabAnsicht gerufen: steht diese Seite gerade vorn? */
export function setSeiteVorn(vorn: () => boolean): void {
  setContext(VORN, vorn);
}

/** Steht die Seite gerade vorn? Die Shell hält zuletzt gezeigte Tabs
 *  verdeckt gebaut (EditorArea, „Gehaltene Tabs"); eine verdeckte Seite
 *  soll weder im Sekundentakt messen noch auf Tasten hören. In der
 *  Initialisierung der Seite rufen -- zurück kommt ein reaktiver Leser.
 *  Ohne Shell (Telefon) immer vorn. */
export function seiteVorn(): () => boolean {
  if (!hasContext(VORN)) return () => true;
  return getContext<() => boolean>(VORN);
}

/** Vorn UND das Browserfenster sichtbar. Für alles, was der Server oder das
 *  Netz bezahlt: Präsenz-Abos („openwrt:live", „hosts:live" takten Agenten
 *  auf 2 s) und schnelle Abrufe. Ein gehaltener Tab oder ein Fenster im
 *  Hintergrund hielt sie sonst dauerhaft am Laufen (Befund KI-Prüfung
 *  21.10.2026). In der Initialisierung der Seite rufen. */
export function seiteSichtbar(): () => boolean {
  const vorn = seiteVorn();
  return () => vorn() && fensterSichtbar();
}

/** Läuft die Seite in der Shell (Schreibtisch)? Am Telefon rendert App.svelte
 *  die Seite ohne Wirt. In der Initialisierung der Seite rufen. */
export function inShell(): boolean {
  return hasContext(KEY);
}

/** Bequemer Zugriff auf einen einzelnen Pfad-Parameter. */
export function pageParam(name: string): string {
  return pageRoute().params[name] ?? "";
}

/** Ein Verweis-Parameter (?focus=, ?auf=), aber nur, wenn die ADRESSE auf
 *  diese Seite zeigt.
 *
 *  router.query gehoert dem ganzen Fenster. Springt man von den Aufgaben
 *  auf "/system/automatiken?focus=Flurlicht", ist die Aufgaben-Seite in
 *  dem Moment noch da, sieht "focus" und wirft ihn aus der Adresse, bevor
 *  die Automatiken ihn lesen koennen -- der Verweis landet auf der vollen
 *  Liste (gemessen 05.10.2026; Netsec schluckte so jeden fremden ?focus=,
 *  solange es in der zweiten Spalte offen war). Seiten mit einem
 *  Verweis-Parameter lesen ihn darum hierueber statt direkt aus
 *  router.query. */
export function seitenQuery(name: string): string | undefined {
  if (tabWurzelFuer(router.current) !== tabWurzelFuer(pageRoute().path)) return undefined;
  return router.query[name];
}
